
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import ItemDetailsV4 from "./item-details.vue";
import CommoditySectionV4 from "./commodity.vue";
import ModalAllCommodityV4 from "./modal-all-commodity.vue";
import PackageWeightSectionV4 from "./package-weight.vue";
import SavedPackageCards from "./saved-package-cards.vue";
import {
  CommodityBookingV4,
  CommodityCategoryV4,
  CommodityCiplBookingV4,
  PieceBookingV4,
  HwsThresholdConfig,
  ErrorBookingV4,
  SavedPackageBookingV4,
  CreateBookingV4
} from "@/domain/entities/BookingV4";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";

type GoodsPackageSectionKeyV4 = "item-details" | "commodity" | "package-weight";

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  showItemDetails = prop<boolean>({ required: true, type: Boolean });
  showResetButton = prop<boolean>({ default: true, type: Boolean });
  showSavePackageCheckbox = prop<boolean>({ default: true, type: Boolean });
  useDefaultDimensions = prop<boolean>({ default: true, type: Boolean });
  isFtz = prop<boolean>({ default: false, type: Boolean });
  localCurrency = prop<"IDR" | "MYR">({ default: "IDR" });
  currencyPrefix = prop<"Rp" | "RM">({ default: "Rp" });
  numericLocale = prop<"id" | "my">({ default: "id" });
  isForeign = prop<boolean>({ default: false, type: Boolean });
  title = prop<string>({ default: "" });
  showTitle = prop<boolean>({ default: true, type: Boolean });
  // Detail Barang (CIPL) §3.6 — passthrough ke item-details.vue (GENESIS-19984)
  commodityCipl = prop<CommodityCiplBookingV4[]>({ default: () => [] });
  // Nama Barang dropdown options (§3.6.2) — deliberately separate from
  // `commodities` below (the Komoditas searchbar's own list), see parent's
  // ciplCommodityOptions for why they can't share one array.
  ciplCommodities = prop<CommodityBookingV4[]>({ default: () => [] });
  estimatedConversion = prop<{ currency: string; amount: number } | null>({
    default: null
  });
  deminimisThreshold = prop<number | null>({ default: null });
  deminimisExceeded = prop<boolean>({ default: false, type: Boolean });
  destinationCurrencyCode = prop<string>({ default: "" });
  recipientImportDocRequired = prop<boolean>({
    default: false,
    type: Boolean
  });
  selfClearanceRequired = prop<boolean>({
    default: false,
    type: Boolean
  });
  // Komoditas — searchbar + chip rekomendasi (GENESIS-19985)
  commodity = prop<CommodityBookingV4 | null>({ default: null });
  commodities = prop<CommodityBookingV4[]>({
    default: () => []
  });
  recommendedCommodities = prop<CommodityBookingV4[]>({
    default: () => []
  });
  savedPackages = prop<SavedPackageBookingV4[]>({
    default: () => []
  });
  savedPackagesLoading = prop<boolean>({ default: false, type: Boolean });
  customerId = prop<number>({ default: 0 });
  errors = prop<ErrorBookingV4>({
    default: () => new ErrorBookingV4(),
    type: Object
  });
  loading = prop<boolean>({ default: false, type: Boolean });
  hasMore = prop<boolean>({ default: false, type: Boolean });

  modalSearchResults = prop<CommodityBookingV4[]>({ default: () => [] });
  commodityCategories = prop<CommodityCategoryV4[]>({
    default: () => []
  });
  commodityCategoryItems = prop<CommodityBookingV4[]>({
    default: () => []
  });
  selectedCategoryCode = prop<string | null>({ default: null });
  modalLoading = prop<boolean>({ default: false, type: Boolean });
  modalLoadingCategory = prop<boolean>({ default: false, type: Boolean });
  modalHasMore = prop<boolean>({ default: false, type: Boolean });
  // Berat Koli §3.8 — passthrough ke package-weight.vue (GENESIS-19986)
  pieces = prop<PieceBookingV4[]>({ default: () => [] });
  volumeWeightDivisor = prop<number>({ required: true });
  hwsConfig = prop<HwsThresholdConfig>({ default: new HwsThresholdConfig() });
  maxPieces = prop<number>({ default: 15, type: Number });
  singlePieceOnly = prop<boolean>({ default: false, type: Boolean });
  disableAddPiece = prop<boolean>({ default: false, type: Boolean });
  // Adjust v4 per-field rules (GENESIS-20182) — optional, default EDITABLE
  configCommodity = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configGoodsPrice = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  // CIPL Detail Barang section rule (GENESIS-20184) — interCipl / ftzCipl
  configCipl = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configGrossWeight = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configDimension = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configAdjustPieceAdd = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  configAdjustPieceRemove = prop<SttAdjustmentRule>({ default: new SttAdjustmentRule(), type: Object });
  isShowSectionUpload = prop<boolean>({
    default: false,
    type: Boolean
  });
  disabledWeightAdjustment = prop<boolean>({
    default: false,
    type: Boolean
  });
  minWeightThreshold = prop<number>({
    default: 0,
    type: Number
  });
  isReverseJourney = prop<boolean>({ default: false, type: Boolean });
  savePackage = prop<boolean>({
    default: false,
    type: Boolean
  });
  hasSenderId = prop<boolean>({
    default: false,
    type: Boolean
  });
  activeSavedPackageId = prop<number | null>({
    default: null,
  });
  isEdit = prop<boolean>({
    default: false,
    type: Boolean
  });
  isMaxChWeight = prop<boolean>({
    default: false,
    type: Boolean
  });
  preventDecreasedWeight = prop<boolean>({
    default: true,
    type: Boolean
  });
  isItemDetailsRequired = prop<boolean>({ required: true, type: Boolean });
  eligibleEditSize = prop<boolean>({
    default: false
  });
}

@Options({
  name: "GoodsPackageV4",
  emits: [
    "reset",
    "ciplValidationChange",
    "commodityValidationChange",
    "packageWeightValidationChange",
    "grossWeightChange",
    "tariffContextChange",
    "update:commodityCipl",
    "update:commodity",
    "update:pieces",
    "update:savePackage",
    "searchCommodity",
    "loadMoreCommodities",
    "searchCommodityModal",
    "loadMoreCommodityModal",
    "selectCommodityCategory",
    "commodityError",
    "selectSavedPackage"
  ],
  components: {
    Checkbox,
    ItemDetailsV4,
    CommoditySectionV4,
    ModalAllCommodityV4,
    PackageWeightSectionV4,
    SavedPackageCards
  }
})
export default class GoodsPackageV4 extends Vue.with(Props) {
  isCommodityModalOpen = false;
  localCommodity: CommodityBookingV4 | null = null;
  localCategoryCode: string | null = null;

  get activeCategoryCode(): string | null {
    return this.selectedCategoryCode ?? this.localCategoryCode;
  }

  get selectedCommodity(): CommodityBookingV4 | null {
    return this.commodity || this.localCommodity;
  }

  get activeCategoryCommodities(): CommodityBookingV4[] {
    if (this.commodityCategoryItems && this.commodityCategoryItems.length > 0) {
      return this.commodityCategoryItems;
    }

    if (!this.activeCategoryCode) return [];

    return this.commodities.filter((item: any) => {
      return (
        item.categoryCode === this.activeCategoryCode ||
        item.code === this.activeCategoryCode
      );
    });
  }

  get mountedSections(): GoodsPackageSectionKeyV4[] {
    return this.showItemDetails
      ? ["item-details", "commodity", "package-weight"]
      : ["commodity", "package-weight"];
  }

  get showSavedPackageCards(): boolean {
    return !this.isEdit && !this.isReverseJourney;
  }

  onSelectCategory(categoryCode: string) {
    this.localCategoryCode = categoryCode;
    this.$emit("selectCommodityCategory", categoryCode);
  }

  onResetClick() {
    const isWeightAdjustmentLocked =
      this.isShowSectionUpload && this.disabledWeightAdjustment;
    const isCiplNonEditable = this.configCipl.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
    const isCommodityNonEditable = this.configCommodity.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
    const isGrossWeightNonEditable =
      isWeightAdjustmentLocked ||
      this.configGrossWeight.rule.includes(ConfigValidationForm.NON_EDITABLE);
    const isDimensionNonEditable =
      isWeightAdjustmentLocked ||
      this.configDimension.rule.includes(ConfigValidationForm.NON_EDITABLE);
    const currentCipl = this.commodityCipl.map(
      (item) => new CommodityCiplBookingV4(item)
    );
    const currentCommodity = this.selectedCommodity
      ? new CommodityBookingV4(this.selectedCommodity)
      : null;
    const currentPieces = this.pieces.map((piece) =>
      new PieceBookingV4(piece)
    );

    if (!isCommodityNonEditable) this.localCommodity = null;
    this.$emit("reset", this.mountedSections);

    if (isCiplNonEditable) {
      this.$emit("update:commodityCipl", currentCipl);
    }

    if (isCommodityNonEditable) {
      this.$emit("update:commodity", currentCommodity);
    }

    if (
      currentPieces.length &&
      (isGrossWeightNonEditable || isDimensionNonEditable)
    ) {
      this.$emit(
        "update:pieces",
        this.buildPiecesAfterReset(
          currentPieces,
          isGrossWeightNonEditable,
          isDimensionNonEditable
        )
      );
    }
  }

  buildPiecesAfterReset(
    pieces: PieceBookingV4[],
    preserveGrossWeight: boolean,
    preserveDimension: boolean
  ): PieceBookingV4[] {
    const defaultLength = this.useDefaultDimensions ? 3 : 0;
    const defaultWidth = this.useDefaultDimensions ? 20 : 0;
    const defaultHeight = this.useDefaultDimensions ? 10 : 0;
    return pieces.map((piece) => {
      const length = preserveDimension ? piece.length : defaultLength;
      const width = preserveDimension ? piece.width : defaultWidth;
      const height = preserveDimension ? piece.height : defaultHeight;

      return new PieceBookingV4({
        length,
        width,
        height,
        grossWeight: preserveGrossWeight ? piece.grossWeight : 0,
        volumeWeight: preserveDimension
          ? piece.volumeWeight
          : this.volumeWeightDivisor
          ? (length * width * height) / this.volumeWeightDivisor
          : 0
      });
    });
  }

  onCommodityUpdate(commodity: CommodityBookingV4 | null) {
    this.localCommodity = commodity;
    this.$emit("update:commodity", commodity);
  }

  onModalSelect(commodity: CommodityBookingV4) {
    this.localCommodity = commodity;
    this.$emit("update:commodity", commodity);
    this.isCommodityModalOpen = false;
    if (commodity.code) {
      this.$emit("commodityError", "");
    }
  }

  onModalClose() {
    this.isCommodityModalOpen = false;
    this.localCategoryCode = null;
  }

  onCiplValidationChange(isValid: boolean) {
    this.$emit("ciplValidationChange", isValid);
  }

  onCommodityValidationChange(isValid: boolean) {
    this.$emit("commodityValidationChange", isValid);
  }

  onPackageWeightValidationChange(isValid: boolean) {
    this.$emit("packageWeightValidationChange", isValid);
  }

  touchAllItemDetailsRows() {
    const itemDetails = this.$refs.itemDetailsRef as any;
    if (itemDetails && typeof itemDetails.touchAllRows === "function") {
      itemDetails.touchAllRows();
    }
  }

  resetItemDetailsTouchedFields() {
    const itemDetails = this.$refs.itemDetailsRef as any;
    if (itemDetails && typeof itemDetails.resetTouchedFields === "function") {
      itemDetails.resetTouchedFields();
    }
  }
}
